/**
 * PaystubBrandingSection — Batch #14.268 : apparence du bulletin de paie (logo de l'employeur sous son nom, cotisations employeur optionnelles).
 */
import { useRef, useState } from "react";
import axios from "axios";
import { FileImage, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, Section, Btn, Check, err } from "./_shared";

export default function PaystubBrandingSection({ masterId, s, set, onLogoChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/payroll/${masterId}/logo`, fd, { headers: { ...authH(), "Content-Type": "multipart/form-data" } });
      set("logo_url", r.data.logo_url);
      toast.success("Logo enregistré — il apparaîtra sous le nom de l'employeur sur les bulletins");
      onLogoChanged?.();
    } catch (e) { err(e); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Retirer le logo des bulletins de paie ?")) return;
    setBusy(true);
    try { await axios.delete(`${API}/admin/payroll/${masterId}/logo`, { headers: authH() }); set("logo_url", ""); toast.success("Logo retiré"); onLogoChanged?.(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <Section title="Bulletin de paie — apparence" icon={FileImage} testId="paie-settings-paystub">
      <div className="grid sm:grid-cols-[auto_1fr] gap-4 items-start">
        <div className="w-44 h-20 border-2 border-dashed border-slate-300 rounded-xl bg-white flex items-center justify-center overflow-hidden" data-testid="paie-logo-preview">
          {s.logo_url
            ? <img src={`${API}${s.logo_url.replace(/^\/api/, "")}?v=${encodeURIComponent(s.logo_url)}`} alt="Logo de l'employeur" className="max-w-full max-h-full object-contain p-1" data-testid="paie-logo-img" />
            : <span className="text-[11px] text-slate-400 text-center px-2">Aucun logo<br />(facultatif)</span>}
        </div>
        <div className="space-y-2">
          <p className="text-xs text-slate-600">Logo de l'entreprise (le client) imprimé <strong>sous son nom</strong> en haut à gauche des bulletins. PNG ou JPG, max 2 Mo — idéalement un logo horizontal sur fond blanc.</p>
          <div className="flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="paie-logo-file" />
            <Btn tone="slate" icon={ImagePlus} onClick={() => fileRef.current?.click()} busy={busy} testId="paie-logo-upload-btn">{s.logo_url ? "Remplacer le logo" : "Téléverser un logo"}</Btn>
            {s.logo_url && <Btn tone="slate" icon={Trash2} onClick={remove} busy={busy} testId="paie-logo-remove-btn">Retirer</Btn>}
          </div>
        </div>
      </div>
      <div className="pt-2 border-t">
        <Check label="Afficher le tableau « Cotisations de l'employeur » sur le bulletin (RRQ, AE, RQAP, FSS, CNESST…) — décocher pour ne montrer à l'employé que ses gains et retenues"
          checked={s.paystub_show_employer !== false} onChange={(v) => set("paystub_show_employer", v)} testId="paie-settings-show-employer" />
      </div>
    </Section>
  );
}
